/**
 * 1.4.6: 组织切换器组件
 * 允许用户在多个组织之间切换当前工作的组织
 * 切换后会刷新项目列表，显示新组织的项目
 * 1.5.1: 添加工作区名称多语言支持
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ChevronDown, Check, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  getUserOrganizations,
  getCurrentOrganization,
  setCurrentOrganization,
} from '../services/organizationService';
import { logger } from '../utils/logger';
import { formatOrganizationName } from '../utils/organizationUtils'; // 1.5.1
import type { UserOrganization } from '../types/organization';

interface OrganizationSwitcherProps {
  isCollapsed?: boolean;
  onOrganizationChange?: () => void; // 组织切换后的回调
}

export function OrganizationSwitcher({
  isCollapsed = false,
  onOrganizationChange,
}: OrganizationSwitcherProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<UserOrganization[]>([]);
  const [currentOrg, setCurrentOrg] = useState<UserOrganization | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const menuRef = useRef<HTMLDivElement>(null);

  // 加载组织列表
  useEffect(() => {
    const loadOrganizations = async () => {
      setIsLoading(true);
      try {
        const { organizations: orgs, error } = await getUserOrganizations();
        if (error) {
          logger.error('Error loading organizations:', error);
          return;
        }
        setOrganizations(orgs);

        // 获取当前组织
        const { organization } = await getCurrentOrganization();
        setCurrentOrg(organization);
      } catch (error) {
        logger.error('Error loading organizations:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadOrganizations();
  }, []);

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  // 切换组织
  const handleSwitchOrganization = (org: UserOrganization) => {
    if (org.id === currentOrg?.id) {
      setShowMenu(false);
      return;
    }

    // 设置新的当前组织
    setCurrentOrganization(org.id);
    setCurrentOrg(org);
    setShowMenu(false);

    // 通知父组件刷新数据
    if (onOrganizationChange) {
      onOrganizationChange();
    }

    // 1.4.6: 刷新页面以确保所有数据重新加载
    window.location.href = '/app';
  };

  // 如果只有一个组织或正在加载，不显示切换器
  if (isLoading || organizations.length <= 1) {
    return null;
  }

  // 折叠模式下只显示图标
  if (isCollapsed) {
    return (
      <div className="p-2 border-b border-gray-200">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mx-auto">
          <Building2 className="w-5 h-5 text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 border-b border-gray-200 relative" ref={menuRef}>
      <div className="text-xs text-gray-500 mb-2">{t('currentOrganization')}</div>
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="w-full flex items-center justify-between p-2 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <Building2 className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0 text-left">
            <div className="text-sm font-medium text-gray-900 truncate">
              {currentOrg ? formatOrganizationName(currentOrg.name, t) : t('selectOrganization')}
            </div>
            {currentOrg && (
              <div className="text-xs text-gray-500 flex items-center gap-1">
                <Users className="w-3 h-3" />
                <span>
                  {currentOrg.role === 'owner' ? t('owner') : t('member')}
                </span>
              </div>
            )}
          </div>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${
            showMenu ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* 组织下拉菜单 */}
      {showMenu && (
        <div className="absolute top-full left-0 right-0 mt-1 mx-4 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
          {organizations.map((org) => (
            <button
              key={org.id}
              onClick={() => handleSwitchOrganization(org)}
              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${
                currentOrg?.id === org.id ? 'bg-primary/5' : ''
              }`}
            >
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  currentOrg?.id === org.id
                    ? 'bg-primary/20'
                    : 'bg-gray-100'
                }`}
              >
                <Building2
                  className={`w-4 h-4 ${
                    currentOrg?.id === org.id ? 'text-primary' : 'text-gray-500'
                  }`}
                />
              </div>
              <div className="flex-1 min-w-0 text-left">
                <div
                  className={`text-sm font-medium truncate ${
                    currentOrg?.id === org.id ? 'text-primary' : 'text-gray-900'
                  }`}
                >
                  {formatOrganizationName(org.name, t)}
                </div>
                <div className="text-xs text-gray-500 flex items-center gap-2">
                  <span>
                    {org.org_type === 'business' ? t('business') : t('personal')}
                  </span>
                  <span>·</span>
                  <span>
                    {org.role === 'owner' ? t('owner') : t('member')}
                  </span>
                </div>
              </div>
              {currentOrg?.id === org.id && (
                <Check className="w-4 h-4 text-primary flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
